// Marker-Manipulation in der Markdown-Quelle (für Click-to-Edit in PreviewPane).
//
// Eingabe: aktueller rawMarkdown, ein Block-Offset (aus data-md-start vom DOM),
// gewünschte Action.
// Ausgabe: neuer rawMarkdown + neuer Offset, an dem der betroffene Block jetzt
// im Quelltext beginnt. Den Offset nutzt der Preview, um nach dem Reparse zur
// gleichen Stelle zurückzuscrollen statt zum nächsten Kapitelanfang zu springen.
//
// Idempotenz: wenn ein passender Marker direkt vor dem Block schon existiert
// (innerhalb von ~200 Zeichen), wird kein weiterer eingefügt, sondern der Typ
// ggf. ersetzt. „remove" entfernt einen vorhandenen Marker.
const PAGEBREAK_RE = /(?:^|\n)\s*::pagebreak(\{[^}]*\})?\s*(?=\n|$)/;
const KEEP_OPEN_RE = /(?:^|\n)\s*:::keep\s*(?=\n|$)/;
const FULLPAGE_OPEN_RE = /(?:^|\n)\s*:::fullpage\s*(?=\n|$)/;
/**
 * Sucht rückwärts ab `offset` einen bestehenden Marker. Liefert die Match-
 * Bereichs-Indizes (start, end) im Original-Text oder null.
 */
function findMarkerBefore(md, offset, re, windowSize = 200) {
    const from = Math.max(0, offset - windowSize);
    const slice = md.slice(from, offset);
    let last = null;
    const r = new RegExp(re.source, "g");
    let m;
    while ((m = r.exec(slice)) !== null) {
        last = m;
    }
    if (!last)
        return null;
    return {
        start: from + last.index,
        end: from + last.index + last[0].length,
        matchedText: last[0],
    };
}
export function applyBlockAction(md, offset, action) {
    if (offset < 0 || offset > md.length)
        return { md, newOffset: offset };
    // Bildbreite: `offset` ist hier das Ende der Bild-Syntax (direkt hinter der
    // schließenden `)`). Wir setzen/ändern den unmittelbar folgenden Pandoc-
    // Attribut-Block `{…}` und erhalten dabei evtl. vorhandene Klassen/IDs.
    if (action.kind === "image-width" || action.kind === "image-reset") {
        const after = md.slice(offset);
        const m = after.match(/^\{([^}]*)\}/);
        const existingLen = m ? m[0].length : 0;
        const tokens = (m ? m[1] : "")
            .split(/\s+/)
            .filter((t) => t && !/^width\s*=/i.test(t));
        if (action.kind === "image-width")
            tokens.push(`width=${action.pct}%`);
        const inner = tokens.join(" ");
        const replacement = inner ? `{${inner}}` : "";
        const next = md.slice(0, offset) + replacement + md.slice(offset + existingLen);
        return { md: next, newOffset: offset };
    }
    if (action.kind === "remove-marker-before") {
        const pb = findMarkerBefore(md, offset, PAGEBREAK_RE);
        if (pb) {
            const next = md.slice(0, pb.start) + md.slice(pb.end);
            return { md: next, newOffset: offset - (pb.end - pb.start) };
        }
        const keep = findMarkerBefore(md, offset, KEEP_OPEN_RE);
        if (keep) {
            const after = md.slice(keep.end);
            const closeMatch = after.match(/(?:^|\n)\s*:::\s*(?=\n|$)/);
            if (closeMatch && typeof closeMatch.index === "number") {
                const closeStart = keep.end + closeMatch.index;
                const closeLen = closeMatch[0].length;
                const next = md.slice(0, keep.start) +
                    md.slice(keep.end, closeStart) +
                    md.slice(closeStart + closeLen);
                return {
                    md: next,
                    newOffset: offset - (keep.end - keep.start),
                };
            }
            const next = md.slice(0, keep.start) + md.slice(keep.end);
            return { md: next, newOffset: offset - (keep.end - keep.start) };
        }
        const fp = findMarkerBefore(md, offset, FULLPAGE_OPEN_RE);
        if (fp) {
            const after = md.slice(fp.end);
            const closeMatch = after.match(/(?:^|\n)\s*:::\s*(?=\n|$)/);
            if (closeMatch && typeof closeMatch.index === "number") {
                const closeStart = fp.end + closeMatch.index;
                const closeLen = closeMatch[0].length;
                const next = md.slice(0, fp.start) +
                    md.slice(fp.end, closeStart) +
                    md.slice(closeStart + closeLen);
                return {
                    md: next,
                    newOffset: offset - (fp.end - fp.start),
                };
            }
            const next = md.slice(0, fp.start) + md.slice(fp.end);
            return { md: next, newOffset: offset - (fp.end - fp.start) };
        }
        return { md, newOffset: offset };
    }
    if (action.kind === "pagebreak") {
        const marker = action.type === "page" ? "::pagebreak" : `::pagebreak{${action.type}}`;
        const insert = `\n\n${marker}\n\n`;
        const existing = findMarkerBefore(md, offset, PAGEBREAK_RE);
        if (existing) {
            const next = md.slice(0, existing.start) + insert + md.slice(existing.end);
            // Block-Offset um (insert.length - existing.length) verschieben
            const delta = insert.length - (existing.end - existing.start);
            return { md: next, newOffset: offset + delta };
        }
        const next = md.slice(0, offset) + insert + md.slice(offset);
        return { md: next, newOffset: offset + insert.length };
    }
    if (action.kind === "keep" || action.kind === "fullpage") {
        const openTag = action.kind === "keep" ? ":::keep" : ":::fullpage";
        const re = action.kind === "keep" ? KEEP_OPEN_RE : FULLPAGE_OPEN_RE;
        if (findMarkerBefore(md, offset, re)) {
            return { md, newOffset: offset };
        }
        const after = md.slice(offset);
        let endRel = -1;
        let countBlanks = 0;
        const re2 = /\n\s*\n/g;
        let m;
        while ((m = re2.exec(after)) !== null) {
            countBlanks++;
            if (countBlanks === 2) {
                endRel = m.index;
                break;
            }
        }
        const blockEnd = endRel >= 0 ? offset + endRel : md.length;
        const before = md.slice(0, offset);
        const block = md.slice(offset, blockEnd);
        const restAfter = md.slice(blockEnd);
        const prefix = `\n${openTag}\n`;
        const next = `${before}${prefix}${block}\n:::\n${restAfter}`;
        // Block startet jetzt hinter dem öffnenden Container-Tag
        return { md: next, newOffset: offset + prefix.length };
    }
    return { md, newOffset: offset };
}
